/**
 * Audio Edge Smoothing Utilities for Studio Recording
 *
 * Eliminates transient DC clicks and microphone pop at the head/tail of takes:
 * 1. applyEdgeSmoothingToBuffer: Applies smooth Hann (raised cosine) micro-fades (15-25ms)
 *    to both head and tail of an AudioBuffer in-place.
 * 2. audioBufferToWavBlob: Encodes an AudioBuffer into a pristine 16-bit PCM WAV Blob
 *    for zero-pop local playback and cloud storage.
 * 3. smoothRecordedTake: Decodes raw recorded take blob, applies raised-cosine edge smoothing,
 *    and produces smoothed AudioBuffer and WAV Blob.
 */

export function applyEdgeSmoothingToBuffer(buffer: AudioBuffer, fadeMs: number = 50): AudioBuffer {
  const sampleRate = buffer.sampleRate;
  const fadeSamples = Math.min(
    Math.floor(sampleRate * (fadeMs / 1000)),
    Math.floor(buffer.length / 4)
  );

  const numChannels = buffer.numberOfChannels;
  for (let ch = 0; ch < numChannels; ch++) {
    const data = buffer.getChannelData(ch);
    const len = data.length;
    if (len === 0) continue;

    // Step 1: Remove DC offset across channel to eliminate DC step / thump
    let sum = 0;
    for (let i = 0; i < len; i++) {
      sum += data[i];
    }
    const dcOffset = sum / len;
    if (Math.abs(dcOffset) > 1e-5) {
      for (let i = 0; i < len; i++) {
        data[i] -= dcOffset;
      }
    }

    if (fadeSamples <= 0) continue;

    // Step 2: Head Fade-in (0.0 to 1.0) using raised-cosine (Hann half-window)
    // Guarantees sample 0 is mathematically 0.0000 with continuous derivative.
    for (let i = 0; i < fadeSamples; i++) {
      const factor = 0.5 * (1 - Math.cos((Math.PI * i) / fadeSamples));
      data[i] *= factor;
    }
    data[0] = 0.0;

    // Step 3: Tail Fade-out (1.0 to 0.0)
    for (let i = 0; i < fadeSamples; i++) {
      const idx = len - 1 - i;
      const factor = 0.5 * (1 - Math.cos((Math.PI * i) / fadeSamples));
      data[idx] *= factor;
    }
    data[len - 1] = 0.0;
  }

  return buffer;
}

export function audioBufferToWavBlob(buffer: AudioBuffer): Blob {
  const numChannels = buffer.numberOfChannels;
  const sampleRate = buffer.sampleRate;
  const bitDepth = 16;
  const numSamples = buffer.length;
  const blockAlign = numChannels * (bitDepth / 8);
  const byteRate = sampleRate * blockAlign;
  const dataSize = numSamples * blockAlign;
  const totalSize = 44 + dataSize;

  const arrayBuffer = new ArrayBuffer(totalSize);
  const view = new DataView(arrayBuffer);

  function writeString(offset: number, str: string) {
    for (let i = 0; i < str.length; i++) {
      view.setUint8(offset + i, str.charCodeAt(i));
    }
  }

  // RIFF header
  writeString(0, 'RIFF');
  view.setUint32(4, 36 + dataSize, true);
  writeString(8, 'WAVE');

  // fmt chunk
  writeString(12, 'fmt ');
  view.setUint32(16, 16, true); // Chunk size 16 for PCM
  view.setUint16(20, 1, true);  // Audio format 1 = PCM
  view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, byteRate, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, bitDepth, true);

  // data chunk
  writeString(36, 'data');
  view.setUint32(40, dataSize, true);

  // Write 16-bit PCM samples
  const channels: Float32Array[] = [];
  for (let c = 0; c < numChannels; c++) {
    channels.push(buffer.getChannelData(c));
  }

  let offset = 44;
  for (let i = 0; i < numSamples; i++) {
    for (let c = 0; c < numChannels; c++) {
      const sample = Math.max(-1, Math.min(1, channels[c][i]));
      const int16 = sample < 0 ? sample * 0x8000 : sample * 0x7FFF;
      view.setInt16(offset, int16, true);
      offset += 2;
    }
  }

  return new Blob([arrayBuffer], { type: 'audio/wav' });
}

export async function smoothRecordedTake(
  audioBlob: Blob,
  audioCtx: AudioContext,
  fadeMs: number = 50
): Promise<{ smoothedBlob: Blob; smoothedBuffer: AudioBuffer }> {
  const arrayBuf = await audioBlob.arrayBuffer();
  const decoded = await audioCtx.decodeAudioData(arrayBuf.slice(0));
  applyEdgeSmoothingToBuffer(decoded, fadeMs);
  const smoothedBlob = audioBufferToWavBlob(decoded);
  return { smoothedBlob, smoothedBuffer: decoded };
}
